import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { computed, nextTick, onMounted, reactive, ref } from "vue"

  import { TLogin } from "@/components/Conta"
  import LSimples from "@/layouts/LSimples/LSimples.vue"
  import PButton from "primevue/button"

  import { useVuelidate } from "@vuelidate/core"
  import { AutenticacaoStore } from "@/store/autenticacao/autenticacao.store"
  import { estados, STATUS } from "@/types/api"
  import { storeToRefs } from "pinia"
  import { useRouter } from "vue-router"
  import { useToast } from "primevue/usetoast"
  import { MModalDeAlerta, IAlertaMensagem } from "@/components/Gerais"
  import { useI18n } from "vue-i18n"
  import {
    consumirErroKeycloak,
    iniciarLoginKeycloak,
  } from "@/servicos/keycloak.service"
  import AutenticacaoRepositorio from "@/repositorios/autenticacao/autenticacao.repositorio"
  import { AmbienteDoUsuarioResposta } from "@/repositorios/autenticacao/autenticacao.modelo"

  interface Props {
    teleportarPara?: string
    // Permitem abrir o login do Timesheet a partir do calendário no Gestão.
    emailInicial?: string
    rotaDeRetorno?: string
    iniciarDescobertaAutomaticamente?: boolean
  }

  interface AlertaComReset extends IAlertaMensagem {
    $reset(): void
  }

  
export default /*#__PURE__*/_defineComponent({
  __name: 'Login',
  props: {
    teleportarPara: { default: undefined },
    emailInicial: { default: "" },
    rotaDeRetorno: { default: "/" },
    iniciarDescobertaAutomaticamente: { type: Boolean, default: false }
  },
  setup(__props: any) {

const props = __props as { teleportarPara: string, emailInicial: string, rotaDeRetorno: string, iniciarDescobertaAutomaticamente: boolean }

  let objetoDialog = reactive<AlertaComReset>({
    titulo: "",
    icone: "",
    visivel: false,
    conteudo: "",
    tipo: "padrao",
    $reset: function () {
      this.visivel = false
      this.titulo = ""
      this.icone = ""
      this.conteudo = ""
    },
  })

  

  const router = useRouter()

  const dadosLogin = reactive({
    email: props.emailInicial.toLowerCase(),
    senha: "",
    $reset: function () {
      this.email = ""
      this.senha = ""
    },
  })

  const { t } = useI18n()

  const toast = useToast()

  const { logar } = AutenticacaoStore()
  const { estado } = storeToRefs(AutenticacaoStore())
  const escopo = "login_vuelidate"
  const v$ = useVuelidate({ $scope: escopo })
  const etapa = ref<"email" | "senha">("email")
  const descobrindoAmbiente = ref(false)
  const limparFormularioAoFecharModal = ref(false)
  const autenticacaoRepositorio = new AutenticacaoRepositorio()
  const tentativasMaximasDeDescoberta = 3

  const carregando = computed(
    () => descobrindoAmbiente.value || estado.value === estados.PEDENTE
  )

  const rotaDeRetorno = computed(() => {
    return props.rotaDeRetorno.startsWith("/") &&
      !props.rotaDeRetorno.startsWith("//")
      ? props.rotaDeRetorno
      : "/"
  })

  const fecharModal = (): void => {
    objetoDialog.$reset()
    if (limparFormularioAoFecharModal.value) {
      dadosLogin.$reset()
      etapa.value = "email"
      v$.value.$reset()
    }
    limparFormularioAoFecharModal.value = false
  }

  const exibirErro = (mensagem: string, limparFormulario = false): void => {
    objetoDialog.titulo = t("naoFoiPossivelEntrar")
    objetoDialog.conteudo = t(mensagem)
    objetoDialog.visivel = true
    objetoDialog.tipo = "erro"
    limparFormularioAoFecharModal.value = limparFormulario
  }

  const validarCamposVisiveis = async (): Promise<boolean> => {
    v$.value.$touch()
    if (await v$.value.$validate()) {
      return true
    }

    toast.add({
      severity: "error",
      summary: t("camposInvalidos"),
      detail: t("completeOsCamposCorretamente"),
      closable: true,
      life: 3000,
    })
    return false
  }

  const liberarLoginPorSenha = async (): Promise<void> => {
    etapa.value = "senha"
    await nextTick()
    v$.value.$reset()
  }

  const descobrirAmbienteDoUsuario =
    async (): Promise<AmbienteDoUsuarioResposta> => {
      for (
        let tentativa = 1;
        tentativa <= tentativasMaximasDeDescoberta;
        tentativa += 1
      ) {
        try {
          return await autenticacaoRepositorio.descobrirAmbienteDoUsuario(
            dadosLogin.email.toLowerCase()
          )
        } catch (erro) {
          if (tentativa === tentativasMaximasDeDescoberta) {
            throw erro
          }
        }
      }

      throw new Error("Não foi possível descobrir o ambiente do usuário")
    }

  const continuarComEmail = async (): Promise<void> => {
    if (!(await validarCamposVisiveis())) {
      return
    }

    descobrindoAmbiente.value = true
    try {
      const ambiente = await descobrirAmbienteDoUsuario()

      if (ambiente.url_de_autenticacao && ambiente.keycloak_realm) {
        await iniciarLoginKeycloak(ambiente, rotaDeRetorno.value)
        return
      }

      await liberarLoginPorSenha()
    } catch {
      // Mantém o fallback já adotado pelo Gestão: indisponibilidade da
      // descoberta não remove a possibilidade de autenticação por senha.
      await liberarLoginPorSenha()
    } finally {
      descobrindoAmbiente.value = false
    }
  }

  const voltarParaEmail = async (): Promise<void> => {
    etapa.value = "email"
    dadosLogin.senha = ""
    await nextTick()
    v$.value.$reset()
  }

  onMounted(() => {
    const erroKeycloak = consumirErroKeycloak()
    if (!erroKeycloak) {
      return
    }

    exibirErro(erroKeycloak, true)
  })

  onMounted(() => {
    if (props.iniciarDescobertaAutomaticamente && dadosLogin.email) {
      void continuarComEmail()
    }
  })

  const tentarLogar = async (): Promise<void> => {
    if (await validarCamposVisiveis()) {
      const resultado = await logar(
        dadosLogin.email.toLowerCase(),
        dadosLogin.senha
      )

      if (
        resultado?.status === "erro" &&
        resultado?.codigo === STATUS.naoAutorizado
      ) {
        exibirErro("oUsuarioOuSenhaEstaoIncorretos", true)
      }

      if (
        resultado?.status === "erro" &&
        resultado?.codigo === STATUS.naoPermitido
      ) {
        exibirErro("usuarioSemAcessoAoTimesheet", true)
      }

      if (resultado?.status === "sucesso" && estado.value === estados.SUCESSO) {
        router.push(rotaDeRetorno.value)
      }
      return
    }
  }

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(LSimples, {
      class: "Login",
      "data-testid": "Login"
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(TLogin), {
          email: dadosLogin.email,
          "onUpdate:email": _cache[0] || (_cache[0] = ($event: any) => ((dadosLogin.email) = $event)),
          emailModifiers: { trim: true },
          senha: dadosLogin.senha,
          "onUpdate:senha": _cache[1] || (_cache[1] = ($event: any) => ((dadosLogin.senha) = $event)),
          senhaModifiers: { trim: true },
          onConfirmarForm: tentarLogar,
          onContinuarComEmail: continuarComEmail,
          onVoltarParaEmail: voltarParaEmail,
          carregando: _unref(carregando),
          "data-testid": "Login__Form",
          "escopo-vuelidate": escopo,
          etapa: etapa.value
        }, null, 8, ["email", "senha", "carregando", "etapa"])
      ]),
      _: 1
    }),
    _createVNode(_unref(MModalDeAlerta), {
      modelValue: _unref(objetoDialog).visivel,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(objetoDialog).visivel) = $event)),
      cabecalho: _unref(objetoDialog).titulo,
      "data-testid": "Login__Modal",
      icone: _unref(objetoDialog).icone,
      "teleportar-para": __props.teleportarPara,
      tipo: _unref(objetoDialog).tipo
    }, {
      conteudo: _withCtx(() => [
        _createTextVNode(_toDisplayString(_unref(objetoDialog).conteudo), 1)
      ]),
      acoes: _withCtx(() => [
        _createVNode(_unref(PButton), {
          onClick: fecharModal,
          class: "NovoProfissional__Button p-button-danger",
          "data-testid": "Login__BotaoFecharModal",
          label: "ok"
        })
      ]),
      _: 1
    }, 8, ["modelValue", "cabecalho", "icone", "teleportar-para", "tipo"])
  ], 64))
}
}

})